import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Inbox,
  MessageSquare,
  Mail,
  Calendar,
  Copy,
  Loader2,
  Filter as FilterIcon,
} from "lucide-react";
import logoLight from "../assets/logo-light.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "foxel_admin_token";

// ------- Login Screen -------
const LoginScreen = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter email and password.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(STORAGE_KEY, data.token);
      toast.success("Signed in.");
      onSuccess(data.token, data.email);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid credentials.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[#0F0914] bg-grid-dark relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(200,90,84,0.15), transparent 60%)" }} />
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md rounded-3xl bg-white border border-[#E5DEE5] shadow-2xl overflow-hidden"
      >
        <div className="header-gradient px-8 py-6 flex items-center gap-3">
          <img src={logoLight} alt="FOXEL" className="h-10 w-auto object-contain" />
          <div className="text-white">
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-white/70">Studio Dashboard</div>
            <div className="font-display font-bold text-lg">Enquiry Inbox</div>
          </div>
        </div>
        <div className="p-8 space-y-6">
          <div>
            <label className="block text-[11px] tracking-[0.25em] uppercase font-semibold text-[#4D454A] mb-2">
              Admin Email
            </label>
            <input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@studio.com"
              className="w-full bg-transparent border-0 border-b-2 border-[#d8cfd8] focus:border-[#c85a54] outline-none py-3 text-[#1A1518] placeholder:text-[#b3a8b8] transition-colors"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.25em] uppercase font-semibold text-[#4D454A] mb-2">
              Password
            </label>
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-transparent border-0 border-b-2 border-[#d8cfd8] focus:border-[#c85a54] outline-none py-3 text-[#1A1518] placeholder:text-[#b3a8b8] transition-colors"
              autoComplete="current-password"
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full brand-gradient text-white uppercase tracking-widest text-xs font-bold px-8 py-4 hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-center text-[10px] tracking-[0.25em] uppercase text-[#8a828a]">
            Private area — Studio access only
          </p>
        </div>
      </motion.form>
    </div>
  );
};

// ------- Small UI helpers -------
const StatCard = ({ label, total, week, icon: Icon }) => (
  <div className="rounded-2xl border border-[#E5DEE5] bg-white p-5 md:p-6 flex items-start justify-between">
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#8a828a]">
        {label}
      </div>
      <div className="mt-2 font-display font-black text-[#4a1c40] text-3xl md:text-4xl leading-none">
        {total}
      </div>
      <div className="mt-1 text-[#c85a54] text-xs font-semibold">
        {week} in the last 7 days
      </div>
    </div>
    <div className="w-11 h-11 rounded-xl brand-gradient text-white flex items-center justify-center shrink-0">
      <Icon size={20} strokeWidth={1.6} />
    </div>
  </div>
);

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const copy = (text) => {
  try {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Could not copy");
  }
};

// ------- Contacts Table -------
const ContactsTable = ({ rows }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(t) ||
        r.email.toLowerCase().includes(t) ||
        (r.project || "").toLowerCase().includes(t)
    );
  }, [rows, q]);

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E5DEE5] p-16 text-center text-[#8a828a]">
        No contact submissions yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <FilterIcon size={14} className="text-[#8a828a]" />
        <input
          data-testid="contacts-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, or project keyword…"
          className="flex-1 bg-transparent border-0 border-b border-[#E5DEE5] focus:border-[#c85a54] outline-none py-2 text-sm text-[#1A1518]"
        />
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#8a828a] font-semibold">
          {filtered.length} / {rows.length}
        </span>
      </div>

      <div className="grid gap-4">
        {filtered.map((c) => (
          <div
            key={c.id}
            data-testid={`contact-row-${c.id}`}
            className="rounded-2xl border border-[#E5DEE5] bg-white p-5 md:p-6"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display font-bold text-[#1A1518] text-lg md:text-xl uppercase tracking-tight truncate">
                  {c.name}
                </div>
                <div className="mt-1 flex items-center gap-2 text-sm text-[#4D454A]">
                  <Mail size={14} className="text-[#c85a54]" />
                  <a
                    href={`mailto:${c.email}`}
                    className="hover:text-[#c85a54] transition-colors truncate"
                  >
                    {c.email}
                  </a>
                  <button
                    onClick={() => copy(c.email)}
                    className="text-[#8a828a] hover:text-[#c85a54]"
                    aria-label="Copy email"
                  >
                    <Copy size={12} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#8a828a] font-semibold shrink-0">
                <Calendar size={12} />
                {formatDate(c.created_at)}
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-[#F4F1F5] p-4 text-[#1A1518] text-sm leading-relaxed whitespace-pre-wrap">
              {c.project}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={`mailto:${c.email}?subject=${encodeURIComponent("Re: Your FOXEL enquiry")}&body=${encodeURIComponent(`Hi ${c.name},\n\nThank you for reaching out to FOXEL Architects.\n\n`)}`}
                className="inline-flex items-center gap-2 rounded-full brand-gradient text-white text-[11px] tracking-widest uppercase font-bold px-4 py-2 hover:opacity-90"
              >
                <Mail size={12} /> Reply by Email
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Hi ${c.name}, following up on your FOXEL enquiry.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white text-[11px] tracking-widest uppercase font-bold px-4 py-2 hover:bg-[#20b358]"
              >
                <MessageSquare size={12} /> WhatsApp
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ------- WA Clicks Table -------
const WaTable = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E5DEE5] p-16 text-center text-[#8a828a]">
        No WhatsApp handoffs recorded yet.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[#E5DEE5] bg-white overflow-hidden">
      <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E5DEE5] text-[10px] tracking-[0.25em] uppercase text-[#8a828a] font-semibold bg-[#F4F1F5]">
        <div className="col-span-3">When</div>
        <div className="col-span-2">Source</div>
        <div className="col-span-5">Project</div>
        <div className="col-span-2 text-right">IP</div>
      </div>
      <div className="divide-y divide-[#E5DEE5]">
        {rows.map((w) => (
          <div
            key={w.id}
            data-testid={`wa-row-${w.id}`}
            className="grid grid-cols-12 gap-4 px-5 py-4 items-center text-sm"
          >
            <div className="col-span-3 text-[#1A1518]">{formatDate(w.created_at)}</div>
            <div className="col-span-2">
              <span className="inline-block rounded-full bg-[#F4F1F5] border border-[#E5DEE5] text-[10px] tracking-widest uppercase text-[#4a1c40] font-bold px-2.5 py-1">
                {w.source}
              </span>
            </div>
            <div className="col-span-5 text-[#4D454A] truncate">
              {w.project_title || <span className="text-[#8a828a] italic">general enquiry</span>}
            </div>
            <div className="col-span-2 text-right text-[#8a828a] text-xs font-mono">
              {w.ip || "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ------- Dashboard -------
const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [wa, setWa] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, w, s] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/wa-clicks`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setContacts(c.data);
      setWa(w.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired, please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F1F5] bg-dots-light">
      {/* Header */}
      <header className="header-gradient text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoLight} alt="FOXEL" className="h-9 w-auto object-contain" />
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-white/70">
                Studio Dashboard
              </div>
              <div className="font-display font-bold text-base">Enquiry Inbox</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="hidden sm:inline-flex text-white/80 hover:text-white text-xs tracking-widest uppercase font-semibold"
            >
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 hover:bg-white hover:text-[#4a1c40] transition-colors px-4 py-2 text-[11px] tracking-widest uppercase font-bold"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-14">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <StatCard
            label="Contact Enquiries"
            total={stats?.contacts.total ?? "—"}
            week={stats?.contacts.last_7d ?? 0}
            icon={Inbox}
          />
          <StatCard
            label="WhatsApp Handoffs"
            total={stats?.wa_clicks.total ?? "—"}
            week={stats?.wa_clicks.last_7d ?? 0}
            icon={MessageSquare}
          />
        </div>

        {/* Tabs */}
        <div className="mt-10 flex items-center gap-2 border-b border-[#E5DEE5]">
          {[
            { key: "contacts", label: `Contact Enquiries (${contacts.length})` },
            { key: "wa", label: `WhatsApp Handoffs (${wa.length})` },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-xs tracking-widest uppercase font-bold border-b-2 transition-colors ${
                tab === t.key
                  ? "text-[#4a1c40] border-[#c85a54]"
                  : "text-[#8a828a] border-transparent hover:text-[#4a1c40]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="mt-8">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#8a828a]">
              <Loader2 size={22} className="animate-spin mr-3" />
              Loading data…
            </div>
          ) : tab === "contacts" ? (
            <ContactsTable rows={contacts} />
          ) : (
            <WaTable rows={wa} />
          )}
        </div>
      </main>
    </div>
  );
};

// ------- Top-level Admin -------
export const AdminPanel = () => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));

  // Optional: verify existing token on mount; if invalid, clear it.
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    toast.success("Signed out.");
  };

  if (!token) return <LoginScreen onSuccess={(t) => setToken(t)} />;
  return <Dashboard token={token} onLogout={logout} />;
};
